'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Group, Container, Paper, Alert, Stack } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useTranslations, useLocale } from 'next-intl';
import DiseaseInformationStep from '../DiseaseInformationStep/DiseaseInformationStep';
import PackagesStep from '../PackagesStep/PackagesStep';
import LocationStep, { LocationData } from '../LocationStep/LocationStep';
import PaymentStep from '../PaymentStep/PaymentStep';
import ConfirmationStep from '../ConfirmationStep/ConfirmationStep';
import HealthGoalsStep from '../HealthGoalsStep/HealthGoalsStep';
import LabTestStep from '../LabTestStep/LabTestStep';
import styles from '../../styles/Stepper.module.css';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import {
  saveHealthGoals,
  saveDiseaseInformation,
  saveLabTest,
  savePackage,
  saveLocation,
  savePayment,
  saveConfirmation,
} from '@/services/apiServices';
import { useUserId } from '@/contexts/UserContext';

interface RecommendedTest {
  'CPT Code': string;
  Container: string;
  'Price (AED)': string;
  'Sample Type': string;
  TAT: string;
  'Test ID': number;
  'Test Name': string;
  reason: string;
}

const steps = [
  'healthGoals',
  'diseaseInformation',
  'labTest',
  'package',
  'location',
  'payment',
  'confirmation',
];

const Prescreening: React.FC = () => {
  const t = useTranslations('steps');
  const locale = useLocale();
  const isRTL = locale === 'ar' || locale === 'he';
  const [active, setActive] = useState(0);
  const [initialRender, setInitialRender] = useState(true);
  const [healthGoals, setHealthGoals] = useState<string[]>([]);
  const [diseaseTypes, setDiseaseTypes] = useState<string[]>([]);
  const [selectedTests, setSelectedTests] = useState<RecommendedTest[]>([]);
  const [location, setLocation] = useState<LocationData | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  const [insuranceDetails, setInsuranceDetails] = useState<string>('');
  const [hasLabTest, setHasLabTest] = useState<boolean>(false);
  const [labTestFiles, setLabTestFiles] = useState<File[]>([]);
  const [hasMedicalReport, setHasMedicalReport] = useState<boolean>(false);
  const [medicalReportFiles, setMedicalReportFiles] = useState<File[]>([]);
  const [resultsSummary, setResultsSummary] = useState<string>('');
  const [showAlert, setShowAlert] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emiratesIdFile, setEmiratesIdFile] = useState<File | null>(null);
  const [insuranceCardFile, setInsuranceCardFile] = useState<File | null>(null);

  const userId = useUserId();

  const stepperRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    if (stepperRef.current && !isMobile) {
      centerActiveStep(stepperRef.current, active, isRTL);
    }
    if (initialRender) {
      setInitialRender(false);
    }
  }, [active, isMobile, isRTL, initialRender]);

  useEffect(() => {
    if (showAlert) {
      const timeout = setTimeout(() => {
        setShowAlert(false);
      }, 5000);

      return () => clearTimeout(timeout);
    }
  }, [showAlert]);

  const validateStep = useCallback(() => {
    switch (active) {
      case 0:
        return healthGoals.length > 0;
      case 1:
        return diseaseTypes.length > 0;
      case 2:
        return true; // Adjust if needed for LabTestStep validation
      case 3:
        return selectedTests.length > 0;
      case 4:
        return location !== null && location.formattedAddress !== '';
      case 5:
        return (
          paymentMethod !== null && (paymentMethod === 'self-pay' || insuranceDetails.length > 0)
        );
      case 6:
        return true; // Adjust if needed for ConfirmationStep validation
      default:
        return false;
    }
  }, [active, healthGoals, diseaseTypes, selectedTests, location, paymentMethod, insuranceDetails]);

  const saveStepData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      switch (active) {
        case 0:
          await saveHealthGoals({ healthGoals, userId });
          break;
        case 1:
          await saveDiseaseInformation({ diseaseTypes, userId });
          break;
        case 2:
          const formData = new FormData();
          formData.append('hasLabTest', hasLabTest.toString());
          formData.append('hasMedicalReport', hasMedicalReport.toString());
          if (userId) {
            formData.append('userId', userId);
          } else {
            console.error('userId is null');
          }
          labTestFiles.forEach((file) => {
            formData.append(`labTestFiles`, file);
          });
          medicalReportFiles.forEach((file) => {
            formData.append(`medicalReportFiles`, file);
          });
          await saveLabTest(formData);
          break;
        case 3:
          await savePackage({ selectedTests, userId });
          break;
        case 4:
          if (location) {
            await saveLocation({
              location: {
                ...location,
                formattedAddress: location.formattedAddress || '',
              },
              userId,
            });
          }
          break;
        case 5:
          const paymentFormData = new FormData();
          paymentFormData.append('paymentMethod', paymentMethod || '');
          paymentFormData.append('insuranceDetails', insuranceDetails);
          if (userId) {
            paymentFormData.append('userId', userId);
          } else {
            console.error('userId is null');
          }
          if (emiratesIdFile) {
            paymentFormData.append('emiratesIdFile', emiratesIdFile);
          }
          if (insuranceCardFile) {
            paymentFormData.append('insuranceCardFile', insuranceCardFile);
          }
          await savePayment(paymentFormData);
          break;
        case 6:
          await saveConfirmation({ resultsSummary, userId });
          break;
        default:
          break;
      }
      return true;
    } catch (err) {
      setError('Failed to save data. Please try again.');
      console.error('API Error:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [
    active,
    healthGoals,
    diseaseTypes,
    hasLabTest,
    hasMedicalReport,
    labTestFiles,
    medicalReportFiles,
    selectedTests,
    location,
    paymentMethod,
    insuranceDetails,
    emiratesIdFile,
    insuranceCardFile,
    resultsSummary,
    userId,
  ]);

  const nextStep = useCallback(async () => {
    if (validateStep()) {
      const didSaveSucceed = await saveStepData();
      if (didSaveSucceed) {
        if (active === steps.length - 2) {
          const summary = `
Health Goals: ${healthGoals.join(', ')}
Disease Types: ${diseaseTypes.join(', ')}
Selected Tests: ${selectedTests.map((test) => test['Test Name']).join(', ')}
Location: ${location ? location.formattedAddress : t('notProvided')}
Payment Method: ${paymentMethod || t('notSelected')}
Insurance Details: ${insuranceDetails || t('notProvided')}
Has Lab Test: ${hasLabTest ? t('yes') : t('no')}
Has Medical Report: ${hasMedicalReport ? t('yes') : t('no')}
          `.trim();
          setResultsSummary(summary);
        }
        setActive((current) => (current < steps.length - 1 ? current + 1 : current));
      } else {
        setShowAlert(true);
      }
    } else {
      setShowAlert(true);
    }
  }, [
    validateStep,
    saveStepData,
    active,
    healthGoals,
    diseaseTypes,
    selectedTests,
    location,
    paymentMethod,
    insuranceDetails,
    hasLabTest,
    hasMedicalReport,
    t,
  ]);

  const prevStep = useCallback(() => {
    setActive((current) => (current > 0 ? current - 1 : current));
  }, []);

  const handleLocationChange = useCallback((newLocation: LocationData) => {
    setLocation(newLocation);
  }, []);

  const handlePackageSelect = useCallback((selectedPackages: RecommendedTest[]) => {
    setSelectedTests(selectedPackages);
  }, []);

  const handleEmiratesIdFileChange = useCallback((file: File | null) => {
    setEmiratesIdFile(file);
  }, []);

  const handleInsuranceCardFileChange = useCallback((file: File | null) => {
    setInsuranceCardFile(file);
  }, []);

  const renderStepContent = useCallback(() => {
    switch (active) {
      case 0:
        return (
          <HealthGoalsStep
            healthGoals={healthGoals}
            onHealthGoalSelect={(goal) =>
              setHealthGoals((prev) =>
                prev.includes(goal) ? prev.filter((g) => g !== goal) : [...prev, goal]
              )
            }
          />
        );
      case 1:
        return (
          <DiseaseInformationStep
            diseaseTypes={diseaseTypes}
            onDiseaseTypeSelect={(type) =>
              setDiseaseTypes((prev) =>
                prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
              )
            }
          />
        );
      case 2:
        return (
          <LabTestStep
            hasLabTest={hasLabTest}
            labTestFiles={labTestFiles}
            medicalReportFiles={medicalReportFiles}
            onHasLabTestChange={setHasLabTest}
            onLabTestFilesChange={setLabTestFiles}
            hasMedicalReport={hasMedicalReport}
            onHasMedicalReportChange={setHasMedicalReport}
            onMedicalReportFilesChange={setMedicalReportFiles}
          />
        );
      case 3:
        return (
          <PackagesStep
            healthGoals={healthGoals}
            diseaseTypes={diseaseTypes}
            onPackageSelect={handlePackageSelect}
          />
        );
      case 4:
        return <LocationStep location={location} onLocationChange={handleLocationChange} />;
      case 5:
        return (
          <PaymentStep
            insuranceDetails={insuranceDetails}
            onInsuranceDetailsChange={setInsuranceDetails}
            onPaymentMethodChange={setPaymentMethod}
            onEmiratesIdFileChange={handleEmiratesIdFileChange}
            onInsuranceCardFileChange={handleInsuranceCardFileChange}
            insuranceProviders={[
              { name: 'providers.nextcare', image: '/images/nextcare.png' },
              { name: 'providers.daman', image: '/images/daman.png' },
              { name: 'providers.cigna', image: '/images/cigna.png' },
            ]}
          />
        );
      case 6:
        return <ConfirmationStep resultsSummary={resultsSummary} />;
      default:
        return null;
    }
  }, [
    active,
    healthGoals,
    diseaseTypes,
    hasLabTest,
    labTestFiles,
    medicalReportFiles,
    hasMedicalReport,
    location,
    paymentMethod,
    insuranceDetails,
    resultsSummary,
    handlePackageSelect,
    handleLocationChange,
    handleEmiratesIdFileChange,
    handleInsuranceCardFileChange,
  ]);

  return (
    <Container size={isMobile ? 'sm' : 'md'} px={isMobile ? 'xs' : 'md'}>
      <Paper p={isMobile ? 'xs' : 'md'} radius="xl" className={styles.onboardingPaper} style={{ overflow: 'hidden' }}>
        {!isMobile && (
          <div className={styles.stepperWrapper}>
            <div className={styles.stepperScrollContainer}>
              <div ref={stepperRef} className={styles.stepperScroll}>
                {steps.map((step, index) => (
                  <div
                    key={index}
                    className={`${styles.stepperStep} ${active === index ? styles.activeStep : ''}`}
                    onClick={() => setActive(index)}
                  >
                    {t(step)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {isMobile && (
          <div className={styles.mobileStepIndicator}>
            Step {active + 1} of {steps.length}: {t(steps[active])}
          </div>
        )}
        {showAlert && (
          <Alert
            icon={<IconAlertCircle size="1rem" />}
            title={t('validationError')}
            color="red"
            withCloseButton
            onClose={() => setShowAlert(false)}
            mb="md"
          >
            {t('validationMessage')}
          </Alert>
        )}
        {error && (
          <Alert
            icon={<IconAlertCircle size="1rem" />}
            title={t('apiError')}
            color="red"
            withCloseButton
            onClose={() => setError(null)}
            mb="md"
          >
            {error}
          </Alert>
        )}
        <Stack gap={isMobile ? 'sm' : 'md'}>
          <div className={styles.stepContentWrapper}>{renderStepContent()}</div>
          <Group justify="space-between" mt="md" className={styles.buttonGroup}>
            <Button variant="default" disabled={active === 0} onClick={prevStep}>
              {t('previous')}
            </Button>
            <Button onClick={nextStep} loading={isLoading}>
              {active === steps.length - 1 ? t('submit') : t('next')}
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Container>
  );
};

export default Prescreening;

function centerActiveStep(container: HTMLElement, activeStepIndex: number, isRTL: boolean) {
  const stepElements = Array.from(container.children);
  const containerWidth = container.parentElement?.clientWidth || 0;
  const activeStepElement = stepElements[activeStepIndex] as HTMLElement;

  if (activeStepElement) {
    const activeStepWidth = activeStepElement.clientWidth;
    const activeStepOffsetLeft = activeStepElement.offsetLeft;

    const offsetToCenter = containerWidth / 2 - activeStepWidth / 2;
    let finalTransform;

    if (isRTL) {
      const totalStepsWidth = container.scrollWidth;
      const activeStepRightEdge = totalStepsWidth - activeStepOffsetLeft - activeStepWidth;
      finalTransform = activeStepRightEdge - offsetToCenter;
      container.style.justifyContent = 'flex-end';
      container.style.transform = `translateX(${finalTransform}px)`;
    } else {
      finalTransform = -activeStepOffsetLeft + offsetToCenter;
      container.style.justifyContent = 'flex-start';
      container.style.transform = `translateX(${finalTransform}px)`;
    }

    container.style.transition = 'transform 0.5s ease';
  }
}
